import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, CircleMarker, Circle, useMapEvents, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Slider } from '../ui/slider';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2, LocateFixed, MapPin, Plane, Search, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { formatDT } from '../../utils/timezone';

const PinPlacer = ({ onPick }) => {
  useMapEvents({ click: (e) => onPick(e.latlng.lat, e.latlng.lng) });
  return null;
};

const Recenter = ({ lat, lng, radius }) => {
  const map = useMap();
  useEffect(() => {
    if (lat != null && lng != null) {
      const degLat = radius / 111;
      map.fitBounds([[lat - degLat, lng - degLat * 1.4], [lat + degLat, lng + degLat * 1.4]], { padding: [20, 20] });
    }
  }, [lat, lng, radius, map]);
  return null;
};

// Session Geofence Alert config — home centre + radius; sign-ins beyond it alert.
export const HomeRegionSection = ({ api }) => {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [searching, setSearching] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [travelDays, setTravelDays] = useState('7');
  const [pausing, setPausing] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get('/security/home-region')
      .then((r) => { if (alive) setCfg(r.data); })
      .catch(() => { if (alive) setCfg({ enabled: false, lat: null, lng: null, radius_km: 100, label: null, ignore_trusted: false }); });
    return () => { alive = false; };
  }, [api]);

  const set = (patch) => setCfg((c) => ({ ...c, ...patch }));

  const searchPlace = async () => {
    if (!query.trim()) return;
    setSearching(true);
    try {
      const r = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=5&q=${encodeURIComponent(query.trim())}`);
      const list = await r.json();
      setResults(list || []);
      if (!list?.length) toast.info('No places found');
    } catch {
      toast.error('Place search failed');
    } finally {
      setSearching(false);
    }
  };

  const pickResult = (res) => {
    set({ lat: parseFloat(res.lat), lng: parseFloat(res.lon), label: res.display_name?.split(',').slice(0, 2).join(',').trim() });
    setResults([]);
    setQuery('');
  };

  const autoDetect = async () => {
    setDetecting(true);
    try {
      const r = await api.post('/security/home-region/auto-detect');
      set({ lat: r.data.lat, lng: r.data.lng, label: r.data.label });
      toast.success(`Detected ${r.data.label || 'your usual location'} from ${r.data.sign_ins} sign-in${r.data.sign_ins === 1 ? '' : 's'}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not detect your usual sign-in location');
    } finally {
      setDetecting(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put('/security/home-region', cfg);
      setCfg((c) => ({ ...c, ...r.data }));
      toast.success(cfg.enabled ? 'Geofence alert saved' : 'Geofence alert turned off');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const pause = async (days) => {
    setPausing(true);
    try {
      const r = await api.post('/security/home-region/pause', { days });
      set({ paused_until: r.data.paused_until });
      toast.success(days > 0 ? `Travel mode on — geofence paused for ${days} days` : 'Travel mode off — geofence alerts resumed');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update travel mode');
    } finally {
      setPausing(false);
    }
  };

  if (!cfg) return null;
  const hasPin = cfg.lat != null && cfg.lng != null;
  const isPaused = cfg.paused_until && new Date(cfg.paused_until) > new Date();

  return (
    <div className="mt-4 rounded-lg border p-3 space-y-3" data-testid="home-region-section">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <ShieldAlert className="w-4 h-4 text-amber-600 shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-medium">Home region &amp; geofence alert</p>
            <p className="text-xs text-muted-foreground">Alert when your account signs in further than the radius from home</p>
          </div>
        </div>
        <Switch checked={cfg.enabled} onCheckedChange={(v) => set({ enabled: v })} data-testid="geofence-toggle" />
      </div>
      {cfg.enabled && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1 flex-1 min-w-[200px]">
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && searchPlace()}
                placeholder="Search a city or address…"
                className="h-8 text-xs"
                data-testid="geofence-search-input"
              />
              <Button variant="outline" size="sm" className="h-8 px-2" onClick={searchPlace} disabled={searching} data-testid="geofence-search-btn">
                {searching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
              </Button>
            </div>
            <Button variant="outline" size="sm" className="h-8 text-xs gap-1" onClick={autoDetect} disabled={detecting} data-testid="geofence-autodetect-btn">
              {detecting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <LocateFixed className="w-3.5 h-3.5" />}
              Use my usual sign-in location
            </Button>
          </div>
          {results.length > 0 && (
            <div className="rounded-md border divide-y" data-testid="geofence-search-results">
              {results.map((r, i) => (
                <button key={i} type="button" className="w-full text-left px-2.5 py-1.5 text-xs hover:bg-muted flex items-center gap-1.5"
                        onClick={() => pickResult(r)} data-testid={`geofence-search-result-${i}`}>
                  <MapPin className="w-3 h-3 shrink-0 text-muted-foreground" />
                  <span className="truncate">{r.display_name}</span>
                </button>
              ))}
            </div>
          )}
          <div className="h-44 rounded-md overflow-hidden border" data-testid="geofence-map">
            <MapContainer center={hasPin ? [cfg.lat, cfg.lng] : [-25.27, 133.77]} zoom={hasPin ? 8 : 3} style={{ height: '100%', width: '100%' }} attributionControl={false}>
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              <PinPlacer onPick={(lat, lng) => set({ lat, lng, label: `${lat.toFixed(3)}, ${lng.toFixed(3)}` })} />
              {hasPin && (
                <>
                  <Recenter lat={cfg.lat} lng={cfg.lng} radius={cfg.radius_km || 100} />
                  <Circle center={[cfg.lat, cfg.lng]} radius={(cfg.radius_km || 100) * 1000}
                          pathOptions={{ color: '#d97706', fillColor: '#f59e0b', fillOpacity: 0.12 }} />
                  <CircleMarker center={[cfg.lat, cfg.lng]} radius={6}
                                pathOptions={{ color: '#d97706', fillColor: '#f59e0b', fillOpacity: 0.9 }} />
                </>
              )}
            </MapContainer>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            {hasPin ? (
              <Badge variant="outline" className="text-[10px] font-normal gap-1" data-testid="geofence-home-label">
                <MapPin className="w-3 h-3" />{cfg.label || `${cfg.lat.toFixed(3)}, ${cfg.lng.toFixed(3)}`}
              </Badge>
            ) : (
              <span data-testid="geofence-no-pin-hint">Click the map, search a place, or auto-detect to set your home location</span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-xs text-muted-foreground shrink-0">Alert radius</span>
            <Slider value={[cfg.radius_km || 100]} min={50} max={1000} step={25}
                    onValueChange={([v]) => set({ radius_km: v })}
                    className="flex-1 min-w-[140px] max-w-xs" data-testid="geofence-radius-slider" />
            <Badge variant="secondary" className="text-[10px]" data-testid="geofence-radius-value">{cfg.radius_km || 100} km</Badge>
          </div>
          <label className="flex items-center gap-2 text-xs cursor-pointer w-fit">
            <Checkbox checked={cfg.ignore_trusted} onCheckedChange={(v) => set({ ignore_trusted: !!v })} data-testid="geofence-ignore-trusted" />
            Don't alert on remembered devices &amp; networks
          </label>
          <div className="flex flex-wrap items-center gap-2 pt-2 border-t">
            <Plane className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
            <span className="text-xs font-medium">Travel mode</span>            {isPaused ? (
              <>
                <Badge className="text-[10px] bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 hover:bg-amber-100" data-testid="geofence-paused-badge">
                  Paused until {formatDT(cfg.paused_until)}
                </Badge>
                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => pause(0)} disabled={pausing} data-testid="geofence-resume-btn">
                  {pausing ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}Resume now
                </Button>
              </>
            ) : (
              <>
                <Select value={travelDays} onValueChange={setTravelDays}>
                  <SelectTrigger className="h-7 w-24 text-xs" data-testid="geofence-pause-days"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['3', '7', '14', '30'].map((d) => <SelectItem key={d} value={d} data-testid={`geofence-pause-days-${d}`}>{d} days</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => pause(parseInt(travelDays, 10))} disabled={pausing} data-testid="geofence-pause-btn">
                  {pausing ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}Pause alerts
                </Button>
                <span className="text-[10px] text-muted-foreground">pause the geofence while you're legitimately on the road</span>
              </>
            )}
          </div>
          <label className="flex items-center gap-2 text-xs cursor-pointer w-fit">
            <Checkbox checked={cfg.resume_notice} onCheckedChange={(v) => set({ resume_notice: !!v })} data-testid="geofence-resume-notice" />
            Email me a reminder the day my travel-mode pause expires
          </label>
        </div>
      )}
      <p className="text-xs text-muted-foreground" data-testid="geofence-email-footnote">
        When an out-of-region sign-in is detected you'll be alerted in-app and by email to your account email (push follows your notification preferences); admins are also notified via Audit Alerts.
      </p>
      <div className="flex justify-end">
        <Button size="sm" className="h-8 text-xs" onClick={save} disabled={saving} data-testid="geofence-save-btn">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}Save geofence settings
        </Button>
      </div>
    </div>
  );
};
